import React from "react";
import { Request } from "../../hooks/Request";
import { PDFDownloadLink, PDFViewer } from "@react-pdf/renderer";
import CuadroComparativoPDF from "../PDFs/CuadroComparativoPDF";
import { Button } from "../Buttons";
import Circle from "../Animation/Circle";

const listFields = [
  "cotizaciónDeProveedores",
  "descripciónDelPedido",
  "descripciónGeneralDeLaCompra",
  "descripcionDeLaSolicitud",
  "codigoDeInterno",
  "tipoDeImportacion",
  "departamentoSolicitante",
  "revisor",
  "primerAprobador",
  "seleccionarAprobador2",
  "observacionesAlElaborarElCuadroComparativo",
  "observacionesDeLaPrimeraAprobación",
  "comentariosDeLaSegundaAprobación",
  "moneda",
  "noRequiereSegundaAprobacion",
];

export const QuoteComparison = (props) => {
  const { procedureId } = props;
  const { data, loading } = Request({
    urlApi: `/Procedure/${procedureId}/dataValues?${listFields.map((field) => `fieldnames=${field}`).join("&")}`,
  });

  const { data: routeData, loading: loadingRoute } = Request({
    urlApi: `/Tradecruz/${procedureId}/HojaDeRuta`,
  });

  if (!loading || !data || !loadingRoute) return <Circle />;

  // Mapping logic
  const requestItemsRaw = data.descripciónDelPedidoSubDatas || [];
  const suppliersRaw = data.cotizaciónDeProveedoresSubDatas || [];

  const suppliersParsed = suppliersRaw.map((prov, i) => {
    const parsed = prov.description2 ? JSON.parse(prov.description2) : {};
    const itemsTotal =
      parsed.itemsTotal !== undefined
        ? parsed.itemsTotal
        : parsed.items?.reduce(
            (acc, curr) => acc + (Number(curr.costoTotal) || 0),
            0,
          ) || 0;
    const logisticTotal =
      parsed.logisticTotal !== undefined ? parsed.logisticTotal : 0;
    const totalValue =
      prov.numberValue !== undefined
        ? prov.numberValue
        : itemsTotal + logisticTotal;

    return {
      id: prov.id,
      titulo: `PROVEEDOR ${i + 1}`,
      nombre: parsed.label || prov.label,
      total: totalValue,
      itemsTotal: itemsTotal,
      logisticTotal: logisticTotal,
      isRecommended: prov.description3 === "true",
      paymentTerms: parsed.paymentTerms || "",
      incoterms: parsed.incoterms || "",
      deliveryDate: parsed.deliveryDate || "",
      logisticCosts: parsed.logisticCosts || [],
      items: parsed.items || [],
    };
  });

  const recommendedProvider = suppliersParsed.find((s) => s.isRecommended);

  // Map request items to the PDF table structure
  const items = requestItemsRaw.map((reqItem, index) => {
    const parsedReq = reqItem.description2
      ? JSON.parse(reqItem.description2)
      : {};

    return {
      item: (index + 1).toString(),
      cantidad: parsedReq.amount || reqItem.numberValue || "",
      unidad: parsedReq.unit || "",
      detalle: `${reqItem.label} ${parsedReq.desc ? `- ${parsedReq.desc}` : ""}`,
      ofertas: [0, 1, 2].map((i) => {
        const sup = suppliersParsed[i];
        if (!sup) return { cUnit: "", cTotal: "", obs: "" };

        // Match by refSubDataIdPedido
        const offer = sup.items.find(
          (it) => it.refSubDataIdPedido === reqItem.id,
        );

        return {
          provId: sup.id,
          cantidad: offer?.cantidad || "",
          cUnit: offer?.costoUnitario || "",
          cTotal: offer?.costoTotal?.toLocaleString() || "",
          obs: offer?.observaciones || "",
        };
      }),
    };
  });

  // Extract unique logistic cost types across all providers
  const uniqueLogisticCostTypes = Array.from(
    new Set(suppliersParsed.flatMap((s) => s.logisticCosts.map((c) => c.type))),
  ).filter((t) => t); // filter out empty

  const info = {
    departamento: data.departamentoSolicitante || "",
    descripcion: data.descripcionDeLaSolicitud || "",
    nroPedido: data.codigoDeInterno || "",
    codigoDoc: "PR-ADQ-123-R166",
    versionDoc: "5",
    tipoCompraActual: data.tipoDeImportacion || "",
    currency: data.moneda || "USD",
    tiposCompra: [
      "Tubería, accesorios, equipos y maquinarias (incluyendo repuestos) para la venta",
      "Materia Prima",
      "Repuestos y herramientas (Producción, Mantenimiento, Calidad y Proyectos)",
      "Equipos y maquinaria (inversión)",
    ],
    proveedores: [0, 1, 2].map(
      (i) => suppliersParsed[i] || { nombre: "", total: 0, logisticCosts: [] },
    ),
    uniqueLogisticCostTypes,
    items: items,
    recomendado: recommendedProvider,
    justificacion: "", // This field doesn't seem to be in the shared JSON
    firmas: [
      // {
      //   firma: "ELABORADO POR:",
      //   nombre:
      //     routeData?.procedureHistories?.find((h) => h.step === 1)
      //       ?.userInChange || "Gabriela Miranda Garcia",
      //   cargo: "Responsable De Importaciones",
      //   fecha: routeData?.procedureHistories?.find((h) => h.step === 1)
      //     ?.finalDate
      //     ? new Date(
      //         routeData?.procedureHistories?.find((h) => h.step === 1)
      //           .finalDate,
      //       ).toLocaleDateString()
      //     : "",
      //   observaciones: "",
      // },
      // {
      //   firma: "REVISADO POR:",
      //   nombre:
      //     routeData?.procedureHistories?.find((h) => h.step === 4)
      //       ?.userInChange ||
      //     data.revisor ||
      //     "",
      //   cargo: "Jefe Administrativo",
      //   fecha: routeData?.procedureHistories?.find((h) => h.step === 4)
      //     ?.finalDate
      //     ? new Date(
      //         routeData?.procedureHistories?.find((h) => h.step === 4)
      //           .finalDate,
      //       ).toLocaleDateString()
      //     : "",
      //   observaciones: data.observacionesAlElaborarElCuadroComparativo || "",
      // },
      {
        firma: "APROBADO POR:",
        firmaImg: "/images/firma-representante-plastiforte.png",
        nombre: "Gabriela Miranda Garcia",
        cargo: "Responsable De Importaciones",
        fecha: routeData?.procedureHistories?.find((h) => h.step === 5)
          ?.finalDate
          ? new Date(
              routeData?.procedureHistories?.find((h) => h.step === 5)
                .finalDate,
            ).toLocaleDateString()
          : "",
        observaciones: data.observacionesDeLaPrimeraAprobación || "",
      },

      ...(data.noRequiereSegundaAprobacion === "true"
        ? []
        : [
            {
              firma: "APROBADO POR:",
              firmaImg: "/images/zulema_andia.png",
              nombre: "Zulema Andia Heredia",
              cargo: "Jefe Administrativo",
              fecha: routeData?.procedureHistories?.find((h) => h.step === 6)
                ?.finalDate
                ? new Date(
                    routeData?.procedureHistories?.find((h) => h.step === 6)
                      .finalDate,
                  ).toLocaleDateString()
                : "",
              observaciones: data.comentariosDeLaSegundaAprobación || "",
            },
          ]),
    ],
  };

  const pdfDoc = <CuadroComparativoPDF info={info} />;

  return (
    <div className="flex flex-col w-[80vw] bg-white rounded-lg shadow-md overflow-hidden">
      {/* Toolbar */}
      <div className="flex justify-between items-center px-4 py-2 bg-gray-100 border-b border-gray-200">
        <h2 className="text-base font-bold text-gray-800">
          Cuadro Comparativo — {data.codigoDeInterno || procedureId}
        </h2>
        <PDFDownloadLink
          document={pdfDoc}
          fileName={`Cuadro_Comparativo_${data.codigoDeInterno || procedureId}.pdf`}
        >
          {({ loading: pdfLoading }) => (
            <Button className="py-1 px-4 text-sm" disabled={pdfLoading}>
              <i className="fas fa-file-download mr-2"></i>
              {pdfLoading ? "Preparando..." : "Descargar PDF"}
            </Button>
          )}
        </PDFDownloadLink>
      </div>

      {/* Inline PDF Viewer */}
      <PDFViewer width="100%" height="800px" style={{ border: "none" }}>
        {pdfDoc}
      </PDFViewer>
    </div>
  );
};
